import { useApi } from "@/hooks";
import ConfirmDialog from "@Components/admin/confirm-dialog";
import { BybButton } from "@Components/common/byb-ds";
import Dialog from "@Components/dialog";
import Button from "@Components/generic/button";
import Icon from "@Components/generic/icon";
import { AppContext, Events, UnityBridge } from "@Context/common/general/context";
import PhaseOfPlay from "@Enums/PhaseOfPlay";
import PhaseOfPlayMap from "@Enums/PhaseOfPlayMap";
import { Api } from "@Shared/schemas";
import { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";
import { faCloudUploadAlt, faEdit, faPlayCircle, faTimesCircle, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Box, Chip, IconButton, Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { isEmpty } from "lodash-es";
import { useContext, useState } from "react";
import { BybDialog, DialogContentDivider, DialogSection } from "../common";
import DialogInfoRow from "../common/dialog/dialog-info-row";
import { getPlayPositionString } from "./helpers";

interface IScenarioInfoDialog {
  open: boolean;
  scenario: ScenarioResponse;
  onEdit: () => void;
  onClose: () => void;
  onDelete: () => void;
}
const ScenarioInfoDialog = ({ open, scenario, onEdit, onDelete, onClose }: IScenarioInfoDialog) => {
  const { dispatch } = useContext(AppContext);
  const theme = useTheme();
  const [openVersionModal, setOpenVersionModal] = useState(false);
  const [playVersionModal, setPlayVersionModal] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [pushProductionModal, setPushProductionModal] = useState(false);

  const {
    call: publishScenario,
    data: publishScenarioResponse,
    loading: publishLoading,
  } = useApi<typeof Api.Scenarios.PublishScenario>({
    api: Api.Scenarios.PublishScenario,
    request: {
      scenario_id: scenario.doc_id || "",
    },
    onSuccess: () => {
      setPushProductionModal(false);
    },
  });

  const {
    call: deleteScenario,
    data: deleteScenarioResponse,
    loading: deleteLoading,
  } = useApi<typeof Api.Scenarios.DeleteScenario>({
    api: Api.Scenarios.DeleteScenario,
    request: {
      scenario_id: scenario.doc_id || "",
      secure_key: "",
    },
    onSuccess: () => {
      setConfirmDeleteModal(false);
      onDelete();
      onClose();
    },
  });

  const {
    call: getScenario,
    data: getScenarioData,
    loading: getScenarioLoading,
  } = useApi<typeof Api.Scenarios.GetScenario>({
    api: Api.Scenarios.GetScenario,
    request: {
      scenario_id: scenario.doc_id || "",
      is_staging: !scenario.published,
    },
  });

  const pushToProduction = () => {
    if (!scenario || isEmpty(scenario.doc_id)) return;
    publishScenario();
  };

  const handlePlayTest = () => {
    setPlayVersionModal(true);
  };

  const createPlayTestSession = ({ isStaging = true, gameSpeed = 100 }) => {
    if (!scenario) return;

    getScenario({
      scenario_id: scenario.doc_id || "",
      is_staging: isStaging,
    }).then(getResponse => {
      console.log("Got scenario data", getResponse.data);
      fetch(getResponse.data.download_url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      })
        .then(downloadResponse => downloadResponse.json())
        .then(scenarioData => {
          UnityBridge.dispatchWebEvent({
            type: UnityBridge.OutgoingWebEvents.PlayTest,
            payload: {
              scenario: scenarioData,
              // game_speed: gameSpeed      // C# isnt converting this to normalised value, excluding until its fixed in Unity
            },
          });
        });
    });
  };

  const deleteScenarioConfirm = (secureKey: string) => {
    if (!scenario) return;
    deleteScenario({
      scenario_id: scenario.doc_id || "",
      secure_key: secureKey,
    });
  };

  const openScenarioInEditor = (isStaging: boolean) => {
    console.log("Open scenario in editor", scenario, isStaging);
    Events.app.navigate.emit({
      path: "coach/360/builder/",
      data: {
        scenario,
        is_staging: isStaging,
      },
    });
  };

  const handleOpenVersion = (isStaging: boolean) => {
    if (!scenario) return;
    setOpenVersionModal(false);
    openScenarioInEditor(isStaging);
  };

  const handleOpenInEditor = () => {
    if (!scenario) return;
    if (scenario.editor_version_draft && scenario.editor_version_published) {
      setOpenVersionModal(true);
    } else {
      openScenarioInEditor(true);
    }
  };

  return (
    <>
      <BybDialog
        open={open}
        name="scenario"
        fullWidth
        //@ts-ignore
        title={
          <Stack direction="row" justifyContent="space-between">
            <Chip color={scenario.published ? "success" : "warning"} label={scenario.published ? "published" : "draft"} />
            <Stack direction="row" spacing={2}>
              <BybButton onClick={handlePlayTest} icon={<Icon icon={faPlayCircle} size="sm" />} color="secondary" />
              <BybButton onClick={onEdit} icon={<Icon icon={faEdit} size="sm" />} color="secondary" />
              <BybButton onClick={() => setPushProductionModal(true)} icon={<Icon icon={faCloudUploadAlt} size="sm" />} color="secondary" />
              <BybButton onClick={() => setConfirmDeleteModal(true)} icon={<Icon icon={faTrash} size="sm" />} color="secondary" />
            </Stack>
          </Stack>
        }
        onClose={onClose}
        dialogActions={
          <>
            <BybButton onClick={onClose} color="secondary" fullWidth>
              Close
            </BybButton>
            <BybButton onClick={handleOpenInEditor} color="primary" fullWidth>
              Open
            </BybButton>
          </>
        }
      >
        <Stack spacing={4}>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="text3xl700" color="dsBase.v200">
                {scenario.name}
              </Typography>
              <Typography variant="textLg500" color="dsNeutral.v300">
                {`Updated ${dayjs(scenario.updated_at ? scenario.updated_at : scenario.created_at).fromNow()}`}
              </Typography>
            </Stack>
            <Stack spacing={2}>
              <DialogInfoRow label="Positions" value={getPlayPositionString(scenario.play_positions)} />
              <DialogInfoRow label="Phase of play" value={PhaseOfPlayMap[scenario.phase_of_play as PhaseOfPlay]} />
              <DialogInfoRow label="Created" value={dayjs(scenario.created_at).format("DD.MM.YY")} />
              <DialogInfoRow label="ID" value={scenario.id.toString()} />
            </Stack>
          </Stack>
          <DialogContentDivider />
          {scenario.playlists ? (
            <DialogSection title={`Playlists (${scenario.playlists.length})`}>
              {scenario.playlists.map((playlist: any, index: number) => (
                <Stack
                  key={index}
                  direction="row"
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: theme.spacing(2.25, 3),
                    border: "1px solid",
                    borderColor: theme.palette.dsSurface.v300,
                    borderRadius: "24px",
                    cornerShape: "squircle",
                    overflow: "hidden",
                  }}
                >
                  <Typography variant="textLg600" color="dsBase.v200">
                    {playlist.name}
                  </Typography>
                  <IconButton>
                    <Icon icon={faTrash} />
                  </IconButton>
                </Stack>
              ))}
            </DialogSection>
          ) : (
            <Typography variant="textLgItalic" color="dsBase.v200">
              {scenario.description}
            </Typography>
          )}
        </Stack>
      </BybDialog>

      {/* ============ DIALOGS ============ */}

      {/* Open specific scenario version in editor */}
      {openVersionModal && scenario && (
        <Dialog
          open={openVersionModal}
          title={"Which version to open?"}
          message={
            <Stack direction={"row"} spacing={20}>
              <Button
                variant="contained"
                onClick={() => {
                  handleOpenVersion(true);
                }}
              >
                {`Draft`}
              </Button>
              <Button
                variant="contained"
                disabled={!scenario.editor_version_published}
                onClick={() => {
                  handleOpenVersion(false);
                }}
              >
                {`Published`}
              </Button>
            </Stack>
          }
          buttonText={"Cancel"}
          toggleDialog={() => {
            setOpenVersionModal(!openVersionModal);
          }}
          disable={false}
        />
      )}

      {/* Play scenario */}
      {playVersionModal && !!scenario && (
        <Dialog
          open={playVersionModal}
          title={"Which version to play test?"}
          message={
            <Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => createPlayTestSession({ isStaging: true })}
                sx={{ marginRight: theme.spacing(2) }}
              >
                {`Draft`}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={!scenario.editor_version_published}
                onClick={() => createPlayTestSession({ isStaging: false })}
              >
                {`Published`}
              </Button>
            </Box>
          }
          buttonText={"Cancel"}
          toggleDialog={() => {
            setPlayVersionModal(!playVersionModal);
          }}
          disable={false}
        />
      )}

      {/* Delete scenario */}
      {confirmDeleteModal && !!scenario && !deleteLoading && (
        <ConfirmDialog
          open={confirmDeleteModal}
          title={"Confirm Deletion"}
          message={`WARNING! By confiming this operation the scenario "${scenario.name}" will be deleted from the platform and removed from active playlists`}
          textInput={true}
          inputLabel={"Enter Security Key"}
          inputType={"password"}
          icon={faTimesCircle}
          confirm={deleteScenarioConfirm}
          cancel={() => {
            setConfirmDeleteModal(false);
          }}
        />
      )}

      {/* Push scenario to production */}
      {pushProductionModal && !publishLoading && (
        <ConfirmDialog
          open={pushProductionModal}
          title={"Confirm Production Push"}
          message={`This action will make the draft version of the scenario "${scenario.name}" the offical published version. This action cannot be reverted.`}
          icon={faCloudUploadAlt}
          confirm={pushToProduction}
          cancel={() => {
            setPushProductionModal(false);
          }}
        />
      )}
    </>
  );
};

export default ScenarioInfoDialog;
